(function() {
  const sectionSelector = '[data-section-type="site-header"]';
  const OPEN_CLASS = 'shm-menu-open';
  const SUBPANEL_CLASS = 'is-subpanel';
  let savedScrollY = 0;

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
  }

  function getScrollLock() {
    return window.bodyScrollLock;
  }

  function getScrollTarget(section) {
    const subPanel = section.querySelector('.shm__panel[data-panel-id].is-active');
    const panel = subPanel || section.querySelector('.shm__panel--root');
    if (!panel) {
      return section.querySelector('[data-shm-drawer]');
    }
    return panel.querySelector('.shm__panel-body')
      || panel.querySelector('.shm__panel-scroll')
      || panel;
  }

  function syncChromeMetrics(section) {
    const chrome = section.querySelector('[data-shm-chrome]');
    const spacer = section.querySelector('[data-shm-chrome-spacer]');
    if (!chrome) {
      return;
    }
    const rect = chrome.getBoundingClientRect();
    const top = Math.max(Math.round(rect.top), 0);
    const height = Math.round(rect.height);
    const bottom = Math.max(Math.round(rect.bottom), 0);
    section.style.setProperty('--shm-chrome-top', top + 'px');
    section.style.setProperty('--shm-chrome-height', height + 'px');
    section.style.setProperty('--shm-drawer-top', bottom + 'px');
    if (isMobile()) {
      document.body.style.setProperty('--headerHeight', height + 'px');
    }
    if (spacer) {
      spacer.style.height = document.body.classList.contains(OPEN_CLASS) ? height + 'px' : '0';
    }
  }

  function updateDrawerTop(section) {
    syncChromeMetrics(section);
  }

  function applyChromePin(section) {
    const chrome = section.querySelector('[data-shm-chrome]');
    const spacer = section.querySelector('[data-shm-chrome-spacer]');
    if (chrome) {
      chrome.classList.add('is-pinned');
    }
    if (spacer) {
      spacer.style.height = section.style.getPropertyValue('--shm-chrome-height') || '0px';
    }
  }

  function unpinChrome(section) {
    const chrome = section.querySelector('[data-shm-chrome]');
    const spacer = section.querySelector('[data-shm-chrome-spacer]');
    if (chrome) {
      chrome.classList.remove('is-pinned');
    }
    if (spacer) {
      spacer.style.height = '0';
    }
  }

  function lockPageScroll(section) {
    if (!isMobile()) {
      return;
    }

    // Only capture scroll Y on the first lock. refreshMenuScroll re-calls this
    // while body is already fixed (scrollY ≈ 0) — must not clobber savedScrollY.
    if (document.body.style.position !== 'fixed') {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    const scrollLock = getScrollLock();
    const target = getScrollTarget(section);
    if (!scrollLock || !target) {
      return;
    }
    if (section.__shmScrollTarget && section.__shmScrollTarget !== target) {
      scrollLock.enableBodyScroll(section.__shmScrollTarget);
    }
    scrollLock.disableBodyScroll(target, { reserveScrollBarGap: true });
    section.__shmScrollTarget = target;
  }

  function unlockPageScroll(section) {
    const scrollLock = getScrollLock();
    if (scrollLock && section.__shmScrollTarget) {
      scrollLock.enableBodyScroll(section.__shmScrollTarget);
      section.__shmScrollTarget = null;
    } else if (scrollLock) {
      const drawer = section.querySelector('[data-shm-drawer]');
      if (drawer) {
        scrollLock.enableBodyScroll(drawer);
      }
    }

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function refreshMenuScroll(section) {
    if (!document.body.classList.contains(OPEN_CLASS)) {
      return;
    }
    syncChromeMetrics(section);
    lockPageScroll(section);
  }

  function setAccordionOpen(acc, isOpen) {
    if (!acc) {
      return;
    }
    acc.classList.toggle('is-open', isOpen);
    const toggle = acc.querySelector('.shm__acc-toggle');
    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  function resetSubpanelScroll(panel) {
    const body = panel && panel.querySelector('.shm__panel-body');
    if (!body) {
      return;
    }
    if (body.__shmScrollRaf) {
      cancelAnimationFrame(body.__shmScrollRaf);
      body.__shmScrollRaf = null;
    }
    body.scrollTop = 0;
  }

  const ACC_SCROLL_MS = 280;

  function scrollSubpanelToAccordion(acc) {
    const body = acc && acc.closest('.shm__panel[data-panel-id] .shm__panel-body');
    const toggle = acc && acc.querySelector('.shm__acc-toggle');
    if (!body || !toggle) {
      return;
    }

    if (body.__shmScrollRaf) {
      cancelAnimationFrame(body.__shmScrollRaf);
      body.__shmScrollRaf = null;
    }

    function targetTop() {
      const head = body.querySelector('.shm__panel-head');
      const headHeight = head ? head.offsetHeight : 0;
      const top = toggle.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - headHeight;
      const max = body.scrollHeight - body.clientHeight;
      return Math.max(0, Math.min(top, max));
    }

    if (prefersReducedMotion()) {
      body.scrollTop = targetTop();
      return;
    }

    const from = body.scrollTop;
    const start = performance.now();

    function step(now) {
      const p = Math.min((now - start) / ACC_SCROLL_MS, 1);
      body.scrollTop = from + (targetTop() - from) * p;
      if (p < 1) {
        body.__shmScrollRaf = requestAnimationFrame(step);
      } else {
        body.scrollTop = targetTop();
        body.__shmScrollRaf = null;
      }
    }

    body.__shmScrollRaf = requestAnimationFrame(step);
  }

  function closeSiblingAccordions(panel, exceptAcc) {
    if (!panel) {
      return;
    }
    panel.querySelectorAll('.shm__acc.is-open').forEach(function(openAcc) {
      if (openAcc === exceptAcc) {
        return;
      }
      openAcc.classList.add('shm__acc--instant');
      setAccordionOpen(openAcc, false);
      window.requestAnimationFrame(function() {
        openAcc.classList.remove('shm__acc--instant');
      });
    });
  }

  function resetPanelAccordions(panel) {
    if (!panel) {
      return;
    }
    const accordions = panel.querySelectorAll('.shm__acc');
    accordions.forEach(function(acc, index) {
      setAccordionOpen(acc, index === 0);
    });
  }

  function resetPanels(section) {
    const panels = section.querySelector('[data-shm-panels]');
    if (!panels) {
      return;
    }
    panels.classList.remove(SUBPANEL_CLASS);
    section.querySelectorAll('.shm__panel[data-panel-id]').forEach(function(panel) {
      panel.classList.remove('is-active');
      panel.hidden = true;
      resetPanelAccordions(panel);
    });
  }

  function openMenu(section) {
    if (isSearchOpen()) {
      closeMobileSearch(section);
    } else {
      blurMobileSearch(section);
    }

    // Measure before body scroll-lock / pin. After scroll, locking the body first
    // (or unsticking the section) makes getBoundingClientRect() report drawer-top ≈ 0
    // so the menu opens under the chrome.
    syncChromeMetrics(section);

    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add(OPEN_CLASS);
    applyChromePin(section);
    lockPageScroll(section);

    const toggle = section.querySelector('[data-shm-menu-toggle]');
    const drawer = section.querySelector('[data-shm-drawer]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
    }
  }

  function closeMenu(section) {
    document.body.classList.remove(OPEN_CLASS);

    const toggle = section.querySelector('[data-shm-menu-toggle]');
    const drawer = section.querySelector('[data-shm-drawer]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
    }

    unlockPageScroll(section);
    unpinChrome(section);
    resetPanels(section);
  }

  function openSubpanel(section, panelId) {
    const panels = section.querySelector('[data-shm-panels]');
    const target = section.querySelector('.shm__panel[data-panel-id="' + panelId + '"]');
    if (!panels || !target) {
      return;
    }
    section.querySelectorAll('.shm__panel[data-panel-id].is-active').forEach(function(panel) {
      panel.classList.remove('is-active');
      panel.hidden = true;
    });
    target.hidden = false;
    window.requestAnimationFrame(function() {
      target.classList.add('is-active');
      panels.classList.add(SUBPANEL_CLASS);
      refreshMenuScroll(section);
    });

    resetPanelAccordions(target);
    resetSubpanelScroll(target);
  }

  function closeSubpanel(section) {
    const panels = section.querySelector('[data-shm-panels]');
    const active = section.querySelector('.shm__panel[data-panel-id].is-active');
    if (!panels) {
      return;
    }
    panels.classList.remove(SUBPANEL_CLASS);
    if (active) {
      resetSubpanelScroll(active);
      active.classList.remove('is-active');
      window.setTimeout(function() {
        active.hidden = true;
        refreshMenuScroll(section);
      }, prefersReducedMotion() ? 0 : 300);
    } else {
      refreshMenuScroll(section);
    }
  }

  function getSearchApp() {
    const existing = window.theme && window.theme.SearchApp;
    if (existing && typeof existing.showModal === 'function') {
      return existing;
    }

    // Predictive Search custom element
    const predictive = document.querySelector('predictive-search.search');
    if (predictive && typeof predictive.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = predictive;
      return predictive;
    }

    // Tagalys Vue instance mounted on #search--app
    const tagalysEl = document.querySelector('#search--app');
    const tagalysApp = tagalysEl && tagalysEl.__vue__;
    if (tagalysApp && typeof tagalysApp.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = tagalysApp;
      return tagalysApp;
    }

    return null;
  }

  function getSearchPanelElement() {
    const searchApp = getSearchApp();
    if (searchApp) {
      if (searchApp.$el) {
        return searchApp.$el;
      }
      if (searchApp.nodeType === 1) {
        return searchApp;
      }
    }
    return (
      document.querySelector('#search--app') ||
      document.querySelector('predictive-search.search')
    );
  }

  function isSearchOpen() {
    const searchApp = getSearchApp();
    return Boolean(
      (searchApp && searchApp.isVisible) ||
      document.body.classList.contains('search-is-visible')
    );
  }

  function getMobileSearch(section) {
    return section.querySelector('[data-shm-inline-search]');
  }

  function setSearchBackVisible(search, isVisible) {
    const backBtn = search && search.querySelector('[data-shm-search-back]');
    if (!backBtn) {
      return;
    }
    if (isVisible) {
      backBtn.removeAttribute('hidden');
      backBtn.tabIndex = 0;
    } else {
      backBtn.setAttribute('hidden', '');
      backBtn.tabIndex = -1;
    }
  }

  function resetMobileSearchUi(section) {
    const search = getMobileSearch(section);
    if (!search) {
      return;
    }
    search.classList.remove('is-focused');
    setSearchBackVisible(search, false);
    const input = search.querySelector('[data-shm-search-input]');
    if (input && document.activeElement === input) {
      input.blur();
    }
  }

  function syncMobileSearchState(search) {
    const input = search.querySelector('[data-shm-search-input]');
    if (!input) {
      return;
    }
    const hasValue = Boolean(input.value && input.value.trim());
    search.classList.toggle('has-value', hasValue);
  }

  function blurMobileSearch(section) {
    if (isSearchOpen()) {
      closeMobileSearch(section);
      return;
    }
    section.__shmIgnoreSearchFocus = true;
    resetMobileSearchUi(section);
    window.setTimeout(function() {
      section.__shmIgnoreSearchFocus = false;
    }, 100);
  }

  function positionMobileSearch(section) {
    const searchElement = getSearchPanelElement();
    const chrome = section.querySelector('[data-shm-chrome]');

    if (!chrome) {
      return;
    }

    const bottom = Math.max(Math.round(chrome.getBoundingClientRect().bottom), 0);
    // Search-only metric — never write --shm-drawer-top (that belongs to the nav drawer).
    section.style.setProperty('--shm-search-panel-top', bottom + 'px');

    if (searchElement && searchElement.style) {
      // Overlap chrome by 1px so sticky/subpixel scroll never shows a seam.
      searchElement.style.top = Math.max(bottom - 1, 0) + 'px';
      searchElement.style.left = '0';
      searchElement.style.right = '0';
      searchElement.style.width = '100%';
      searchElement.style.maxWidth = 'none';
      searchElement.style.marginLeft = '0';
      searchElement.style.marginRight = '0';
    }
  }

  // After closing via overlay, the same gesture's `click` would hit content
  // underneath. Swallow that one click in capture phase.
  function suppressClickThrough() {
    const suppress = function(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      document.removeEventListener('click', suppress, true);
      document.removeEventListener('mouseup', suppress, true);
    };
    document.addEventListener('click', suppress, true);
    document.addEventListener('mouseup', suppress, true);
    window.setTimeout(function() {
      document.removeEventListener('click', suppress, true);
      document.removeEventListener('mouseup', suppress, true);
    }, 400);
  }

  function getSearchAppInput(app) {
    if (!app) {
      return null;
    }
    if (app.searchInput) {
      return app.searchInput;
    }
    // Tagalys Vue SearchApp
    if (app.$refs && app.$refs.searchQuery) {
      return app.$refs.searchQuery;
    }
    const panel = getSearchPanelElement();
    if (panel && panel.querySelector) {
      return (
        panel.querySelector('#search-input-main') ||
        panel.querySelector('input[name="q"]') ||
        panel.querySelector('input[name="query"]')
      );
    }
    return (
      document.querySelector('#search-input-main') ||
      document.querySelector('#search--app input[name="q"]')
    );
  }

  function syncQueryToSearchApp(headerInput) {
    const app = getSearchApp();
    const appInput = getSearchAppInput(app);
    if (!appInput || !headerInput) {
      return;
    }

    const nextValue = headerInput.value;
    if (appInput.value !== nextValue) {
      appInput.value = nextValue;
    }

    // Tagalys listens for native input events on #search-input-main.
    try {
      appInput.dispatchEvent(new Event('input', { bubbles: true }));
      appInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    } catch (err) {
      // IE-safe no-op; modern storefronts support Event constructor.
    }

    // Predictive Search custom element path.
    if (typeof app.onSearch === 'function') {
      app.onSearch();
    }
  }

  function closeMobileSearch(section) {
    if (section.__shmClosingSearch) {
      return;
    }
    section.__shmClosingSearch = true;
    section.__shmIgnoreSearchFocus = true;

    // Prevent SearchApp.hideModal from restoring focus to the header input
    // (that would re-trigger open via the focus handler).
    if (window.theme) {
      window.theme.lastActiveElement = null;
    }

    const searchApp = getSearchApp();
    if (searchApp && typeof searchApp.hideModal === 'function') {
      if (searchApp.isVisible || document.body.classList.contains('search-is-visible')) {
        searchApp.hideModal();
      }
    } else {
      document.body.classList.remove('search-is-visible');
      const panel = getSearchPanelElement();
      if (panel && panel.classList) {
        panel.classList.remove('is-visible');
      }
    }

    resetMobileSearchUi(section);

    const panel = getSearchPanelElement();
    if (panel && panel.style) {
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.width = '';
      panel.style.maxWidth = '';
      panel.style.marginLeft = '';
      panel.style.marginRight = '';
    }
    section.style.removeProperty('--shm-search-panel-top');

    window.setTimeout(function() {
      section.__shmClosingSearch = false;
      section.__shmIgnoreSearchFocus = false;
    }, 150);
  }

  function openMobileSearch(section, headerInput) {
    if (section.__shmClosingSearch || section.__shmIgnoreSearchFocus) {
      return;
    }

    const searchApp = getSearchApp();
    const search = getMobileSearch(section);

    if (document.body.classList.contains(OPEN_CLASS)) {
      closeMenu(section);
    }

    if (!searchApp) {
      return;
    }

    if (searchApp.isVisible || document.body.classList.contains('search-is-visible')) {
      if (search) {
        search.classList.add('is-focused');
        setSearchBackVisible(search, true);
      }
      positionMobileSearch(section);
      syncQueryToSearchApp(headerInput);
      return;
    }

    window.theme = window.theme || {};

    if (typeof searchApp.showModal === 'function') {
      searchApp.showModal();
    } else if (typeof searchApp.toggleModal === 'function') {
      searchApp.toggleModal();
    } else {
      return;
    }

    // showModal stores the header input as lastActiveElement; clear it so
    // overlay / body close does not re-focus and reopen the panel.
    window.theme.lastActiveElement = null;

    if (search) {
      search.classList.add('is-focused');
      setSearchBackVisible(search, true);
    }

    // Position immediately, then again after announcement/header settle.
    positionMobileSearch(section);

    window.requestAnimationFrame(function() {
      window.requestAnimationFrame(function() {
        positionMobileSearch(section);
        syncQueryToSearchApp(headerInput);
        // SearchApp focuses its own input ~50ms after showModal — reclaim.
        window.setTimeout(function() {
          if (
            headerInput &&
            isSearchOpen() &&
            !section.__shmClosingSearch &&
            !section.__shmIgnoreSearchFocus
          ) {
            headerInput.focus();
            syncQueryToSearchApp(headerInput);
          }
        }, 60);
      });
    });
  }

  function rememberSearchQuery(query) {
    if (!query) {
      return;
    }
    let lastSearches = [];
    try {
      lastSearches = localStorage.getItem('lastSearches')
        ? JSON.parse(localStorage.getItem('lastSearches'))
        : [];
    } catch (err) {
      lastSearches = [];
    }
    lastSearches = lastSearches.filter(function(s) {
      return String(s).toLowerCase() !== query.toLowerCase();
    });
    lastSearches.unshift(query);
    lastSearches = lastSearches.slice(0, 10);
    try {
      localStorage.setItem('lastSearches', JSON.stringify(lastSearches));
    } catch (err) {
      // ignore quota / private mode
    }
  }

  function getActivePlaceholderQuery(search) {
    if (!search || !search.querySelector) {
      return '';
    }
    const active = search.querySelector('[data-ph-word].is-active');
    const fallback = active || search.querySelector('[data-ph-word]');
    if (!fallback) {
      return '';
    }
    const label = fallback.getAttribute('data-label') || fallback.textContent || '';
    return String(label).trim();
  }

  function getPhInterval(section) {
    const raw = section && section.getAttribute('data-ph-interval');
    const value = parseInt(raw, 10);
    return value >= 2000 ? value : 4000;
  }

  function rememberPhLabel(label) {
    if (!label) {
      return;
    }
    try {
      sessionStorage.setItem(PH_LABEL_KEY, label);
    } catch (err) {
      // ignore
    }
  }

  function readActivePhIndex(words) {
    for (let i = 0; i < words.length; i++) {
      if (words[i].classList.contains('is-active')) {
        return i;
      }
    }
    return 0;
  }

  function stopPhRotator(host) {
    if (host && host.__shmPhTimer) {
      window.clearInterval(host.__shmPhTimer);
      host.__shmPhTimer = null;
    }
  }

  function getProviderSearchParam() {
    return window.theme && window.theme.searchProvider === 'tagalys' ? 'query' : 'q';
  }

  function buildSearchUrl(action, param, query) {
    return action + '?' + param + '=' + encodeURIComponent(query);
  }

  function setupMobilePlaceholder(search) {
    const section = search.closest(sectionSelector);
    const words = search.querySelectorAll('[data-ph-word]');
    if (!words.length) {
      return;
    }

    const rotator = search.querySelector('[data-ph-rotator]');
    let index = 0;
    if (rotator && rotator.classList.contains('is-ph-ready')) {
      index = readActivePhIndex(words);
    } else if (window.__shInitPhRotator && rotator) {
      index = window.__shInitPhRotator(rotator);
    } else if (words[0]) {
      words[0].classList.add('is-active');
      if (rotator) {
        rotator.classList.add('is-ph-ready');
      }
    }

    let hovering = false;
    search.addEventListener('mouseenter', function() { hovering = true; });
    search.addEventListener('mouseleave', function() { hovering = false; });
    search.addEventListener('focusin', function() { hovering = true; });
    search.addEventListener('focusout', function() { hovering = false; });

    function paused() {
      return hovering ||
        document.hidden ||
        search.classList.contains('is-focused') ||
        search.classList.contains('has-value') ||
        document.body.classList.contains('search-is-visible');
    }

    function advance() {
      if (paused()) {
        return;
      }
      const current = words[index];
      const nextIndex = (index + 1) % words.length;
      const next = words[nextIndex];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      window.setTimeout(function() {
        current.classList.remove('is-leaving');
      }, 550);

      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      index = nextIndex;
    }

    function startRotator() {
      stopPhRotator(search);
      if (words.length < 2 || prefersReducedMotion()) {
        return;
      }
      search.__shmPhTimer = window.setInterval(advance, getPhInterval(section));
    }

    search.__shPhUnpause = function() {
      hovering = false;
      search.classList.remove('is-focused', 'has-value');
      setSearchBackVisible(search, false);
      document.body.classList.remove('search-is-visible');
      index = readActivePhIndex(words);
      for (let i = 0; i < words.length; i++) {
        words[i].classList.remove('is-leaving');
      }
      startRotator();
    };

    startRotator();
  }

  function refreshMobileBag(section) {
    const target = section.querySelector('.shm__bag-count');
    if (!target) {
      return;
    }

    const count = parseInt(target.textContent, 10) || 0;
    target.classList.toggle('hide', count === 0);

    const bagLink = target.closest('a');
    if (bagLink && bagLink.getAttribute('data-bag-label')) {
      bagLink.setAttribute('aria-label', bagLink.getAttribute('data-bag-label') + ' (' + count + ')');
    }
  }

  function setupMobileSearch(section) {
    const search = getMobileSearch(section);
    if (!search || search.__shmSearchBound) {
      return;
    }
    search.__shmSearchBound = true;

    const input = search.querySelector('[data-shm-search-input]');
    const backBtn = search.querySelector('[data-shm-search-back]');

    if (input) {
      input.addEventListener('focus', function() {
        if (!isMobile() || section.__shmIgnoreSearchFocus || section.__shmClosingSearch) {
          return;
        }
        search.classList.add('is-focused');
        setSearchBackVisible(search, true);
        if (!isSearchOpen()) {
          openMobileSearch(section, input);
        } else {
          positionMobileSearch(section);
          syncQueryToSearchApp(input);
        }
      });
      input.addEventListener('input', function() {
        if (!isMobile() || section.__shmIgnoreSearchFocus || section.__shmClosingSearch) {
          return;
        }
        syncMobileSearchState(search);
        if (!isSearchOpen()) {
          openMobileSearch(section, input);
        } else {
          syncQueryToSearchApp(input);
        }
      });
    }

    if (backBtn) {
      backBtn.addEventListener('mousedown', function(event) {
        // Prevent input blur-before-click losing the close action.
        event.preventDefault();
      });
      backBtn.addEventListener('click', function(event) {
        event.preventDefault();
        closeMobileSearch(section);
      });
    }

    search.addEventListener('submit', function(event) {
      let query = input ? input.value.trim() : '';
      if (!query && input) {
        query = getActivePlaceholderQuery(search);
        if (query) {
          input.value = query;
          syncMobileSearchState(search);
          syncQueryToSearchApp(input);
        }
      }

      if (!query) {
        event.preventDefault();
        return;
      }

      rememberSearchQuery(query);
      rememberPhLabel(query);

      const desiredParam = getProviderSearchParam();
      if (input && input.name !== desiredParam) {
        event.preventDefault();
        window.location.assign(buildSearchUrl(search.getAttribute('action') || '/search', desiredParam, query));
      }
    });

    const onPointerDownOutside = function(event) {
      if (!isMobile()) {
        return;
      }
      const open = isSearchOpen() || (search && search.classList.contains('is-focused'));
      if (!open) {
        return;
      }
      const target = event.target;
      if (!target || !target.closest) {
        return;
      }
      if (target.closest('[data-shm-inline-search]')) {
        return;
      }
      if (target.closest('predictive-search.search, .search.is-visible, #search--app, .tagalys-search-suggestions, .search-suggestions')) {
        return;
      }
      // Hamburger: let the click reach openMenu(), which closes search then opens the drawer.
      if (target.closest('[data-shm-menu-toggle]')) {
        return;
      }
      // Other header chrome (logo, icons, empty bar): fully dismiss search, keep the click.
      if (target.closest('.shm__chrome')) {
        closeMobileSearch(section);
        return;
      }
      // Overlay / page body: dismiss and block click-through.
      suppressClickThrough();
      event.preventDefault();
      event.stopPropagation();
      closeMobileSearch(section);
    };
    document.addEventListener('pointerdown', onPointerDownOutside, true);
    section.__shmSearchOutsideHandler = onPointerDownOutside;

    const onSearchReposition = function() {
      if (isMobile() && isSearchOpen()) {
        positionMobileSearch(section);
      }
    };
    window.addEventListener('scroll', onSearchReposition, { passive: true });
    section.__shmSearchRepositionHandler = onSearchReposition;

    section.__shmSearchObserver = new MutationObserver(function() {
      if (!isMobile()) {
        return;
      }
      const open = isSearchOpen();
      if (open) {
        positionMobileSearch(section);
        if (search) {
          search.classList.add('is-focused');
          setSearchBackVisible(search, true);
        }
      } else if (search && !section.__shmClosingSearch) {
        // Always clear focused UI when SearchApp closes (overlay / Escape),
        // even if the header input still has focus.
        section.__shmIgnoreSearchFocus = true;
        resetMobileSearchUi(section);
        window.setTimeout(function() {
          section.__shmIgnoreSearchFocus = false;
        }, 100);
      }
    });
    section.__shmSearchObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    syncMobileSearchState(search);
    setupMobilePlaceholder(search);
  }

  function setupAccordions(section) {
    section.querySelectorAll('[data-shm-acc]').forEach(function(acc) {
      if (acc.__shmAccBound) {
        return;
      }
      acc.__shmAccBound = true;
      const toggle = acc.querySelector('.shm__acc-toggle');
      if (!toggle) {
        return;
      }
      toggle.addEventListener('click', function() {
        const panel = acc.closest('.shm__panel[data-panel-id]');
        const isOpen = acc.classList.contains('is-open');
        if (!isOpen) {
          closeSiblingAccordions(panel, acc);
        }
        setAccordionOpen(acc, !isOpen);
        if (!isOpen) {
          scrollSubpanelToAccordion(acc);
        }
      });
    });
  }

  function init(section) {
    if (!section || section.__shmInit) {
      return;
    }
    section.__shmInit = true;

    const menuToggle = section.querySelector('[data-shm-menu-toggle]');
    const drawer = section.querySelector('[data-shm-drawer]');
    const overlay = document.querySelector('.bg-overlay');

    if (menuToggle) {
      menuToggle.addEventListener('click', function() {
        if (document.body.classList.contains(OPEN_CLASS)) {
          closeMenu(section);
        } else {
          openMenu(section);
        }
      });
    }

    section.addEventListener('click', function(event) {
      const trigger = event.target.closest('[data-shm-panel-trigger]');
      if (trigger) {
        event.preventDefault();
        openSubpanel(section, trigger.getAttribute('data-shm-panel-trigger'));
        return;
      }
      const back = event.target.closest('[data-shm-panel-back]');
      if (back) {
        event.preventDefault();
        closeSubpanel(section);
      }
    });

    if (overlay) {
      // Search overlay dismiss is handled by document pointerdown outside.
      // This click handler only closes the nav drawer.
      const onOverlayClick = function() {
        if (document.body.classList.contains(OPEN_CLASS)) {
          closeMenu(section);
        }
      };
      overlay.addEventListener('click', onOverlayClick);
      section.__shmOverlayHandler = onOverlayClick;
    }

    section.querySelectorAll('[data-region-currency-trigger]').forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        if (document.body.classList.contains(OPEN_CLASS)) {
          closeMenu(section);
        }
      }, true);
    });

    const onKeydown = function(event) {
      if (event.key !== 'Escape') {
        return;
      }
      if (isSearchOpen()) {
        closeMobileSearch(section);
        return;
      }
      const search = getMobileSearch(section);
      if (search && search.classList.contains('is-focused')) {
        blurMobileSearch(section);
        return;
      }
      if (section.querySelector('.shm__panel[data-panel-id].is-active')) {
        closeSubpanel(section);
        return;
      }
      if (document.body.classList.contains(OPEN_CLASS)) {
        closeMenu(section);
      }
    };
    document.addEventListener('keydown', onKeydown);
    section.__shmKeyHandler = onKeydown;

    const onResize = function() {
      if (document.body.classList.contains(OPEN_CLASS)) {
        refreshMenuScroll(section);
      } else {
        updateDrawerTop(section);
      }
      if (isMobile() && isSearchOpen()) {
        positionMobileSearch(section);
      }
      if (!isMobile() && document.body.classList.contains(OPEN_CLASS)) {
        closeMenu(section);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    section.__shmResizeHandler = onResize;

    setupAccordions(section);
    setupMobileSearch(section);
    updateDrawerTop(section);

    if (section.querySelector('.shm__bag-count')) {
      refreshMobileBag(section);
      const onCartChange = function() {
        refreshMobileBag(section);
      };
      document.addEventListener('cart:rendered', onCartChange);
      document.addEventListener('side-cart:rendered', onCartChange);
      section.__shmCartHandler = onCartChange;
    }
  }

  function destroy(section) {
    if (!section) {
      return;
    }
    if (document.body.classList.contains(OPEN_CLASS)) {
      closeMenu(section);
    }
    if (section.__shmOverlayHandler) {
      const overlay = document.querySelector('.bg-overlay');
      if (overlay) {
        overlay.removeEventListener('click', section.__shmOverlayHandler);
      }
      section.__shmOverlayHandler = null;
    }
    if (section.__shmSearchRepositionHandler) {
      window.removeEventListener('scroll', section.__shmSearchRepositionHandler);
      section.__shmSearchRepositionHandler = null;
    }
    if (section.__shmKeyHandler) {
      document.removeEventListener('keydown', section.__shmKeyHandler);
    }
    if (section.__shmResizeHandler) {
      window.removeEventListener('resize', section.__shmResizeHandler);
    }
    if (section.__shmSearchObserver) {
      section.__shmSearchObserver.disconnect();
      section.__shmSearchObserver = null;
    }
    if (section.__shmSearchOutsideHandler) {
      document.removeEventListener('pointerdown', section.__shmSearchOutsideHandler, true);
      section.__shmSearchOutsideHandler = null;
    }
    const search = getMobileSearch(section);
    if (search && search.__shmPhTimer) {
      window.clearInterval(search.__shmPhTimer);
      search.__shmPhTimer = null;
    }
    if (section.__shmCartHandler) {
      document.removeEventListener('cart:rendered', section.__shmCartHandler);
      document.removeEventListener('side-cart:rendered', section.__shmCartHandler);
      section.__shmCartHandler = null;
    }
    section.__shmInit = false;
  }

  function boot() {
    document.querySelectorAll(sectionSelector).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', function() {
    document.querySelectorAll(sectionSelector).forEach(updateDrawerTop);
  });

  document.addEventListener('shopify:section:load', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    init(node);
  });

  document.addEventListener('shopify:section:unload', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    destroy(node);
  });
})();
